import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getSpendingBySite: () => api.get('/dashboard/spending-by-site'),
  getMaterialTrends: () => api.get('/dashboard/material-trends'),
  getRecentActivities: (limit = 10) => api.get('/dashboard/recent-activities', { params: { limit } }),
  getSpendingTrends: () => api.get('/dashboard/spending-trends'),
  getTopMaterials: (limit = 10, siteId = null) => {
    const params = { limit };
    if (siteId) params.siteId = siteId;
    return api.get('/dashboard/top-materials', { params });
  },
  getSupervisorDashboard: () => api.get('/dashboard/supervisor'),
};

// Sites API
export const siteAPI = {
  getAll: () => api.get('/sites'),
  getById: (id) => api.get(`/sites/${id}`),
  create: (data) => api.post('/sites', data),
  update: (id, data) => api.put(`/sites/${id}`, data),
  updateStatus: (id, status) => api.patch(`/sites/${id}/status`, { status }),  // ⬅️ ADD THIS
  delete: (id) => api.delete(`/sites/${id}`),
  assignSupervisor: (id, data) => api.post(`/sites/${id}/assign`, data),
  removeSupervisor: (siteId, userId) => api.delete(`/sites/${siteId}/assign/${userId}`),
  getStats: (id) => api.get(`/sites/${id}/stats`),
  assignMultipleSupervisors: (id, data) => api.post(`/sites/${id}/assign-supervisors`, data),
  removeSupervisorFromSite: (siteId, userId) => api.delete(`/sites/${siteId}/supervisors/${userId}`),
};

// Materials API
export const materialsAPI = {
  getAll: (params) => api.get('/materials', { params }),
  getById: (id) => api.get(`/materials/${id}`),
  create: (materialData) => api.post('/materials', materialData),
  update: (id, materialData) => api.put(`/materials/${id}`, materialData),
  delete: (id) => api.delete(`/materials/${id}`),
  getCategories: () => api.get('/materials/categories'),
  getBySite: (siteId) => api.get(`/materials/site/${siteId}`),
  getInventory: (siteId) => api.get('/inventory', { params: { siteId } }),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (userData) => api.post('/users', userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: (id) => api.delete(`/users/${id}`),
  assignSite: (userId, siteId) => api.post(`/users/${userId}/assign-site`, { siteId }),
};

// Inventory API
export const inventoryAPI = {
  getAll: (params) => api.get('/inventory', { params }),
  getInventory: (siteId) => api.get('/inventory', { params: { siteId } }),
  getBySite: (siteId) => api.get(`/inventory/site/${siteId}`),
  getByCategory: (categoryId) => api.get(`/inventory/category/${categoryId}`),
  getLowStock: () => api.get('/inventory/low-stock'),
  updateStock: (id, stockData) => api.put(`/inventory/${id}`, stockData),
};

// Reports API
export const reportsAPI = {
  getSiteReport: (siteId, params) => api.get(`/reports/site/${siteId}`, { params }),
  getMaterialReport: (params) => api.get('/reports/materials', { params }),
  getExpenseReport: (params) => api.get('/reports/expenses', { params }),
  exportReport: (type, params) => api.get(`/reports/export/${type}`, { params, responseType: 'blob' }),
};

// Departments API
export const departmentsAPI = {
  getAll: () => api.get('/departments'),
  getById: (id) => api.get(`/departments/${id}`),
  create: (deptData) => api.post('/departments', deptData),
  update: (id, deptData) => api.put(`/departments/${id}`, deptData),
  delete: (id) => api.delete(`/departments/${id}`),
};

// Material Categories API
export const materialCategoriesAPI = {
  getAll: () => api.get('/material-categories'),
  getById: (id) => api.get(`/material-categories/${id}`),
  create: (categoryData) => api.post('/material-categories', categoryData),
  update: (id, categoryData) => api.put(`/material-categories/${id}`, categoryData),
  delete: (id) => api.delete(`/material-categories/${id}`),
};

// Material Items API
export const materialItemsAPI = {
  getAll: () => api.get('/material-items'),
  getById: (id) => api.get(`/material-items/${id}`),
  create: (itemData) => api.post('/material-items', itemData),
  update: (id, itemData) => api.put(`/material-items/${id}`, itemData),
  delete: (id) => api.delete(`/material-items/${id}`),
  getByCategory: (categoryId) => api.get(`/material-items/category/${categoryId}`),
};

// Expense Categories API
export const expenseCategoriesAPI = {
  getAll: () => api.get('/expense-categories'),
  getById: (id) => api.get(`/expense-categories/${id}`),
  create: (categoryData) => api.post('/expense-categories', categoryData),
  update: (id, categoryData) => api.put(`/expense-categories/${id}`, categoryData),
  delete: (id) => api.delete(`/expense-categories/${id}`),
};

// IT Asset Management API
export const itAssetAPI = {
  getEmployees: (params) => api.get('/it-assets/employees', { params }),
  createEmployee: (data) => api.post('/it-assets/employees', data),
  updateEmployee: (id, data) => api.patch(`/it-assets/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/it-assets/employees/${id}`),
  hardDeleteEmployee: (id) => api.delete(`/it-assets/employees/${id}/permanent`),
  restoreEmployee: (id) => api.post(`/it-assets/employees/${id}/restore`),
  getEmployeeHistory: (id) => api.get(`/it-assets/employees/${id}/history`),
  exportAssets: () => api.get('/it-assets/assets/export', { responseType: 'blob' }),
  exportAll: () => api.get('/it-assets/export-all', { responseType: 'blob' }),
  importAssets: (formData) => api.post('/it-assets/assets/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  bulkUpdatePurchaseDates: (formData) => api.post('/it-assets/assets/bulk-update-purchase-dates', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportEmployees: () => api.get('/it-assets/employees/export', { responseType: 'blob' }),
  importEmployees: (formData) => api.post('/it-assets/employees/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportAllotments: () => api.get('/it-assets/allotments/export', { responseType: 'blob' }),
  importAllotments: (formData) => api.post('/it-assets/allotments/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  getAssets: (params) => api.get('/it-assets/assets', { params }),
  createAsset: (data) => api.post('/it-assets/assets', data),
  updateAsset: (id, data) => api.patch(`/it-assets/assets/${id}`, data),
  getStockSummary: () => api.get('/it-assets/assets/stock-summary'),
  deleteAsset: (id) => api.delete(`/it-assets/assets/${id}`),
  hardDeleteAsset: (id) => api.delete(`/it-assets/assets/${id}/permanent`),
  restoreAsset: (id) => api.post(`/it-assets/assets/${id}/restore`),
  uploadInvoice: (id, formData) => api.post(`/it-assets/assets/${id}/upload-invoice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadRentalAgreement: (id, formData) => api.post(`/it-assets/assets/${id}/upload-rental-agreement`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getAssetHistory: (id) => api.get(`/it-assets/assets/${id}/history`),
  reallocateAsset: (id, data) => api.post(`/it-assets/assets/${id}/reallocate`, data),

  getAllotments: (params) => api.get('/it-assets/allotments', { params }),
  createAllotment: (data) => api.post('/it-assets/allotments', data),
  returnAllotment: (id, data) => api.post(`/it-assets/allotments/${id}/return`, data),
  updateAllotment: (id, data) => api.patch(`/it-assets/allotments/${id}`, data),
  deleteAllotment: (id) => api.delete(`/it-assets/allotments/${id}`),
  hardDeleteAllotment: (id) => api.delete(`/it-assets/allotments/${id}/permanent`),
  restoreAllotment: (id) => api.post(`/it-assets/allotments/${id}/restore`),
  uploadSignedDocument: (id, formData) =>
    api.post(`/it-assets/allotments/${id}/upload-signed-document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Backward compatibility aliases
export const materialAPI = materialsAPI;
export const userAPI = usersAPI;
export const departmentAPI = departmentsAPI;

export default api;